/**
 * DCP Distribution + KDM management (v9.22.1).
 *
 * The Distribution page lifted out of the Library. Every per-venue
 * detail card the old Library expanded panel exposed lives here, just
 * scoped to the whole fleet rather than one DCP at a time. KDM page
 * is folded in as a tab.
 *
 * Goals — operator-facing, NOT reduced:
 *   - Group by venue (lowercased email or venue_name): one card per
 *     recipient, even when there's both a KDM and a Share.
 *   - Show every fact the Library used to: KDM validity window, expiry
 *     urgency chips (≤48h amber, expired red), shared-cert warning,
 *     view/started/complete counters with hover legend, transfer
 *     telemetry table (size · % · Mbps · duration · HTTP · IP), share
 *     status (created/sent/expires), and every action icon (copy,
 *     download, email, revoke, resend, delete).
 *   - Plain-language status header per card: Started / Sent · no key /
 *     Awaiting download / Delivered (only when confirmed_count > 0) /
 *     Partial.
 *   - All-DCP top-level filter searches across recipient, DCP, venue.
 *   - Default-collapsed cards (one summary line each) so a full venue
 *     audit doesn't drown the page; click to expand.
 *
 * /kdms is folded in as the second tab; the same KDM History table the
 * standalone page used to render lives there. /kdms route now redirects
 * here. The legacy /dcp/:id/distribute (per-DCP venue-batch KDM) page
 * still exists; the Distribution sidebar item highlights for it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Autocomplete, TextField, Alert, Snackbar, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TableSortLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Block as RevokeIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Launch as OpenIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Archive as PackageIcon,
  Email as ResendIcon,
  TaskAlt as OutcomeIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import PipelineProgress from '../components/PipelineProgress';
import UpcomingScreeningsAlert from '../components/UpcomingScreeningsAlert';
import ActiveTransfersPanel from '../components/ActiveTransfersPanel';
import { CANONICAL_ORIGIN } from '../config';
import DCPDistributeDialog from '../components/DCPDistributeDialog';
import OutstandingVenueRequestsTable from '../components/OutstandingVenueRequestsTable';
import { fetchDCPJobs } from '../store/slices/dcpJobsSlice';
import { fetchPackages, revokeShare } from '../store/slices/deliverySlice';
import {
  fetchKDMRecords, downloadKDM, deleteKDMRecord,
} from '../store/slices/kdmSlice';
import { fetchBookings } from '../store/slices/bookingsSlice';
import api from '../services/api';
import { fmtBytes } from '../utils/downloadStatus';
import { fmtDateTime, fmtDateTimeSeconds, fmtDate } from '../utils/formatDate';
import EmailComposer from '../components/EmailComposer';
import RecordOutcomeDialog from '../components/RecordOutcomeDialog';
import PhysicalDeliveryMarkDialog from '../components/PhysicalDeliveryMarkDialog';
import useSortableTable from '../utils/useSortableTable';
import {
  computeKdmAggregates,
  kdmExpiryStage,
  shouldShowDistributionBulkOps,
  isDistributionBulkOpsForced,
  setDistributionBulkOpsForced,
} from '../utils/library';
import { useBulkRunner } from '../utils/bulkOps';
import BulkActionBar from '../components/BulkActionBar';
import { Checkbox, FormControlLabel, Switch } from '@mui/material';
// v10.21.50: Ship 1 of the DCP UX refactor. Operator asked for theater
// details inline on the Distribution page and the ability to change
// share-link expiry without a backend round-trip.
import TheaterDetailsPanel from '../components/dcp/TheaterDetailsPanel';
import ShareExpiryEditor from '../components/dcp/ShareExpiryEditor';
import { physicalStatus, driveSummaryLabel, driveContents } from '../utils/physicalDelivery';

// ── small helpers ─────────────────────────────────────────────────

// (Removed in v10.4.23: parked `fmtAgo` helper — replaced with the
// shared `formatRelativeTime` from utils/library.js, and the
// unmounted `RecordOutcomeDialog` import that was triggered by an
// unwired callback. Both recoverable from git history if the
// outcome-recording flow lands.)

// fmtBytes: SI-1000-base size formatter — imported from
// utils/downloadStatus. Other pages (Dashboard "0 MB" empty-state,
// UploadManagerOverlay binary base for transfer display, Sources
// using utils/sources.formatBytes for binary base) intentionally use
// different shapes; don't fold those into this one.
function fmtDur(s) {
  if (s == null) return '—';
  if (s < 1) return `${(s * 1000).toFixed(0)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${(s / 60).toFixed(1)}m`;
}

// Plain-language card status. Operator's first read of the row.
function cardStatus({ haveShare, haveKdm, allKeysReady, downloadConfirmed, pivotedToPhysical, cancelled, physicalDelivery }) {
  // v10.17.19: operator-recorded "pivoted to alt delivery" and
  // "cancelled" outcomes win over every other status. They mean
  // "stop tracking the digital — the recipient told us in plain
  // English they're getting a drive (or the screening's off)." Show
  // that explicitly so the card doesn't read as "Awaiting download"
  // for the next 14 days against a delivery that isn't happening.
  // v10.18.18: physical delivery in flight is itself a signal —
  // even without an operator-recorded outcome, if there's a
  // non-cancelled physical_delivery row for this venue+DCP, the
  // card's primary state is the drive's status (shipped, delivered,
  // received, tested).
  if (cancelled) return { label: 'Cancelled', color: 'default', filled: false };
  if (physicalDelivery && physicalDelivery.status && physicalDelivery.status !== 'cancelled') {
    // v12.0.0: shared wording with the operator Physical Drives page.
    const st = physicalStatus(physicalDelivery);
    if (st.key !== 'requested' && st.key !== 'unknown') {
      return { label: driveSummaryLabel(physicalDelivery), color: st.color, filled: true };
    }
  }
  if (pivotedToPhysical) return { label: 'Pivoted to physical', color: 'warning', filled: true };
  if (!haveShare && !haveKdm) return { label: 'Started', color: 'default', filled: false };
  // "Sent · no key" pre-v10.7.1 read ambiguously — operator thought
  // it meant "we don't have the key." It actually means we sent the
  // recipient the share link but haven't generated their KDM yet, so
  // the recipient can't play. Make that unambiguous.
  if (!allKeysReady) return { label: 'Sent · KDM pending', color: 'warning', filled: true };
  if (haveShare && !downloadConfirmed) return { label: 'Awaiting download', color: 'info', filled: false };
  if (downloadConfirmed) return { label: 'Delivered', color: 'success', filled: true };
  return { label: 'Partial', color: 'warning', filled: false };
}

function kdmStatusChip(status) {
  const map = {
    pending:    { label: 'pending',    color: 'default' },
    generating: { label: 'generating', color: 'info' },
    completed:  { label: 'ready',      color: 'success' },
    failed:     { label: 'failed',     color: 'error' },
  };
  return map[status] || { label: status, color: 'default' };
}

// ── per-venue recipient card (all the detail the Library used to show) ──

// v10.18.28: simplified recipient card for venues whose only delivery
// record is an audit-log breadcrumb (parent DCP cancelled, kdm_records
// hard-deleted). Renders alongside live RecipientCards in the main
// list, matching their outer chrome (date tile on the left, venue
// label, status chip) so the visual rhythm holds. The internals are
// stripped to the audit-log facts we still have: dates, recipient,
// "Delivered" success chip — no download / revoke actions because
// there's nothing live to act on.
function HistoricalAuditRecipientCard({ recipient }) {
  const lastTs = recipient.lastTs || null;
  const sends = recipient.historicalSends || [];
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', opacity: 0.92 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center', gap: 1, p: 1.25 }}>
        {/* Date tile — same dimensions as live cards. Anchored on
            most-recent send so cards stay sorted by activity. */}
        <Box sx={{
          minWidth: 64, width: 64, flex: '0 0 64px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center',
          py: 0.5, borderRadius: 1,
          border: 1, borderColor: 'divider', opacity: 0.7,
        }}>
          {lastTs ? (
            <>
              <Typography sx={{ fontSize: '0.6rem', lineHeight: 1, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>
                {new Date(lastTs).toLocaleDateString(undefined, { month: 'short' })}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', lineHeight: 1.1, fontWeight: 700, color: 'text.secondary' }}>
                {new Date(lastTs).getDate()}
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', lineHeight: 1, color: 'text.secondary' }}>
                {new Date(lastTs).getFullYear()}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase' }}>—</Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 220, flex: '0 1 260px' }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {recipient.historicalChain ? `${recipient.historicalChain} ` : ''}{recipient.label}
          </Typography>
          {recipient.email && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {recipient.email}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 180, flex: '0 1 200px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>DCP</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.78rem', color: 'text.disabled', fontStyle: 'italic' }}>
            (historical — DCP since removed)
          </Typography>
        </Box>
        <Chip size="small" label="✓ Delivered" color="success" variant="filled" sx={{ height: 22, fontSize: '0.7rem' }} />
        <Chip size="small" label={`${sends.length} past send${sends.length === 1 ? '' : 's'}`} variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', fontStyle: 'italic' }}>
          from audit log
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, px: 2, pb: 1.25, pl: 10 }}>
        {sends.map((s) => (
          <Typography key={s.id} variant="caption" sx={{ fontSize: '0.72rem' }}>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {new Date(s.sent_at).toLocaleDateString()} {new Date(s.sent_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </Box>
            {' · '}
            <Box component="span">KDM emailed</Box>
            {' → '}
            <Box component="span" sx={{ color: 'success.main' }}>success</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}> · {s.sent_to}</Box>
          </Typography>
        ))}
      </Box>
    </Box>
  );
}


function RecipientCard({
  recipient, dcp, dcpKdms, physicalDelivery,
  transfersByShare, fetchTransfersFor,
  onCopy, onResend, onRevoke, onEmailKdm, onDeleteKdm, onComposeEmail,
}) {
  const screeningTs = recipient.nextScreeningTs;
  const screeningPast = recipient.isPastScreening;
  const [expanded, setExpanded] = useState(false);
  // v10.17.17: re-wires the RecordOutcomeDialog that was unmounted in
  // v10.4.23. The dialog lets the operator log "DCP tested + confirmed"
  // (pre-show) and "Screening successful" (post-show) as distinct
  // events against this recipient. Refresh trigger increments so the
  // outcomes-summary useEffect re-pulls the timeline after a save.
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [outcomesRefresh, setOutcomesRefresh] = useState(0);
  // v10.18.20: which lifecycle mark the dialog opens for. The
  // shared PhysicalDeliveryMarkDialog owns the form-state details.
  const [markKind, setMarkKind] = useState(null);
  // Outcomes state was declared further down (just before the
  // useEffect that pulls it). Hoisted to here in v10.17.19 because
  // cardStatus now reads off the latest outcome to render the
  // "Pivoted to physical" / "Cancelled" chip — the TDZ would otherwise
  // throw at render time. The fetch hook stays in its original
  // position; only the declaration moved.
  const [outcomes, setOutcomes] = useState(null);

  // v10.7.3: a delivery may cover multiple auditoriums and thus carry
  // multiple KDMs. The KDM-related counters here roll up across them
  // so a status of "✓ KDM" only fires when EVERY screen has its key.
  const completedKdms = recipient.kdms.filter((k) => k.status === 'completed');
  const completedKdm = completedKdms[0] || recipient.kdms[0];
  const allKdmsReady = recipient.kdms.length > 0 && completedKdms.length === recipient.kdms.length;
  const someKdmsReady = completedKdms.length > 0;
  const activeShare = recipient.shares.find((s) => !s.revoked_at) || recipient.shares[0];
  const haveKdm = allKdmsReady;  // "we have ALL keys", not just one
  const haveShare = !!activeShare && !activeShare.revoked_at;
  const downloadConfirmed = !!activeShare && (activeShare.confirmed_count || 0) > 0;
  const allKeysReady = dcp?.encrypt ? haveKdm : true;
  // v10.17.19: outcomes that fundamentally change the card's status.
  // Looks at this share's own outcomes (recorded via the dialog with
  // share_id set), not just venue-wide history — a prior cancelled
  // run on this venue shouldn't override the current delivery's
  // status. Falls back to venue-wide outcomes ONLY when an older
  // outcome exists for the share's revoke timestamp (covers the case
  // where the operator clicked Save without share_id).
  const latestShareOutcome = (() => {
    if (!outcomes || !activeShare) return null;
    return outcomes.find((o) => o.share_id === activeShare.id) || null;
  })();
  const pivotedToPhysical = !!(
    activeShare && activeShare.revoked_at
    && latestShareOutcome && latestShareOutcome.outcome === 'pivoted_to_alt_delivery'
  );
  const cancelled = !!(
    latestShareOutcome && latestShareOutcome.outcome === 'cancelled'
  );
  const status = cardStatus({ haveShare, haveKdm, allKeysReady, downloadConfirmed, pivotedToPhysical, cancelled, physicalDelivery });

  // KDM expiry urgency: amber within 48h, red once expired. Roll up
  // across all KDMs — earliest expiry wins (worst case for ops).
  const kdmExpiry = (() => {
    const valid = completedKdms.map((k) => k.valid_to ? new Date(k.valid_to) : null).filter(Boolean);
    if (valid.length === 0) return null;
    return new Date(Math.min(...valid.map((d) => d.getTime())));
  })();
  const hoursToExpiry = kdmExpiry ? (kdmExpiry.getTime() - Date.now()) / 36e5 : null;
  const kdmExpired = hoursToExpiry != null && hoursToExpiry < 0;
  const kdmExpiringSoon = hoursToExpiry != null && hoursToExpiry >= 0 && hoursToExpiry <= 48;

  // Auto-fetch transfer telemetry the first time the card is expanded.
  useEffect(() => {
    if (expanded && activeShare?.id) fetchTransfersFor(activeShare.id);
  }, [expanded, activeShare?.id, fetchTransfersFor]);

  // Recent venue-outcomes inline summary. Appended history of how prior
  // deliveries to this venue went, so the operator scans the card and
  // immediately sees "last attempt: format unknown — sent .tar" without
  // having to leave the page. State declaration moved up earlier in
  // v10.17.19 — see comment above. Fetch hook stays here.
  useEffect(() => {
    if (!recipient.venueId) return;
    // v10.17.19: fetch outcomes on card mount, not only on expand,
    // because the cardStatus chip on the collapsed header now reads
    // "Pivoted to physical" / "Cancelled" off the latest outcome
    // for the share. Waiting until expand to fetch left the chip
    // showing stale "Awaiting download" until the operator opened
    // the card.
    let cancelled = false;
    import('../services/api').then(({ default: api }) => {
      api.get(`/api/venues/${recipient.venueId}/outcomes?limit=5`)
        .then((r) => { if (!cancelled) setOutcomes(r.data || []); })
        .catch(() => { if (!cancelled) setOutcomes([]); });
    });
    return () => { cancelled = true; };
    // outcomesRefresh bumps after a save so the timeline + status
    // chip pick up the new row without forcing a full page reload.
  }, [recipient.venueId, outcomesRefresh]);

  // Shared-cert warning across other KDMs of the same DCP.
  const sharedCertOthers = (() => {
    if (!completedKdm?.recipient_cert_id) return null;
    const dupes = (dcpKdms || []).filter(
      (o) => o.id !== completedKdm.id && o.recipient_cert_id === completedKdm.recipient_cert_id,
    );
    if (dupes.length === 0) return null;
    return dupes.map((o) => `${o.venue_name || '?'} · ${o.screen_name || '?'}`).join(', ');
  })();

  const tfs = activeShare ? transfersByShare[activeShare.id] : undefined;

  // v10.18.24: when the physical drive is the path that actually
  // carried the file (delivered_at / received_at / tested_at), float
  // its line item above Digital. The "successful delivery" should be
  // the first thing the operator sees under DELIVERY — not buried
  // below a Revoked digital share that nobody is using anymore.
  const physicalSucceeded = !!(
    physicalDelivery && (
      physicalDelivery.delivered_at
      || physicalDelivery.received_at
      || physicalDelivery.tested_at
    )
  );
  const physicalFirst = physicalSucceeded;

  return (
    <>
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      {/* Collapsed header — venue + DCP + status + key counters + expand caret.
          v10.17.16: allow children to wrap so the multi-column card
          stays readable on iPhone width without horizontally scrolling
          the entire page. Date tile + recipient stay on the first row;
          DCP name, chips, and counters wrap onto subsequent rows on
          narrow viewports. */}
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: 0.75,
          alignItems: 'center',
          gap: 1, p: 1.25,
          cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
        {/* Screening date + time — leading visual anchor on each card.
            Calendar tear-off styling so the operator's eye lands on
            "when" before "who". Time below the year answers "what
            showtime?" in the same glance. Past screenings render muted;
            the no-screening case (press shares with no KDM) gets a
            placeholder block of the same width so cards stay
            column-aligned. */}
        <Box
          sx={{
            minWidth: 64, width: 64, flex: '0 0 64px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center',
            py: 0.5, borderRadius: 1,
            border: 1,
            borderColor: screeningTs ? (screeningPast ? 'divider' : 'primary.main') : 'divider',
            bgcolor: screeningTs && !screeningPast ? 'action.selected' : 'transparent',
            opacity: screeningTs ? (screeningPast ? 0.55 : 1) : 0.4,
          }}
        >
          {screeningTs ? (
            <>
              <Typography sx={{ fontSize: '0.6rem', lineHeight: 1, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>
                {new Date(screeningTs).toLocaleDateString(undefined, { month: 'short' })}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', lineHeight: 1.1, fontWeight: 700, color: screeningPast ? 'text.secondary' : 'primary.main' }}>
                {new Date(screeningTs).getDate()}
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', lineHeight: 1, color: 'text.secondary' }}>
                {new Date(screeningTs).getFullYear()}
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.2, mt: 0.25, fontWeight: 600, color: screeningPast ? 'text.secondary' : 'text.primary' }}>
                {new Date(screeningTs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase' }}>—</Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 220, flex: '0 1 260px' }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {recipient.label}
            {(() => {
              // v10.7.3: list all auditoriums this delivery services so
              // the operator can see at a glance that ONE delivery
              // covers both Upstairs + Downstairs at Texas, etc.
              const screens = Array.from(new Set(
                (recipient.kdms || [])
                  .map((k) => k.screen_name)
                  .filter(Boolean)
                  .filter((n) => n !== recipient.label),
              ));
              if (screens.length === 0) return null;
              return (
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                  · {screens.join(' + ')}
                </Box>
              );
            })()}
          </Typography>
          {recipient.email && recipient.email !== recipient.label && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {recipient.email}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 180, flex: '0 1 200px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>DCP</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.78rem' }} noWrap>
            {dcp?.name || '—'}{dcp?.encrypt ? ' 🔐' : ''}
          </Typography>
          {/* v10.19.6: an unencrypted DCP is an emergency-backup
              format (no KDM, plays anywhere). Surface as a clear
              warning so the operator never ships one accidentally
              and the producer can see at a glance which deliveries
              went out un-keyed. */}
          {dcp && dcp.encrypt === false && (
            <Tooltip title="Unencrypted DCP — no KDM required, plays on any projector. Emergency / backup format only. Verify the recipient knows this is for emergency use.">
              <Chip
                size="small"
                label="⚠ UNENCRYPTED"
                color="warning"
                variant="filled"
                sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, mt: 0.25, letterSpacing: 0.4 }}
              />
            </Tooltip>
          )}
        </Box>
        <Chip
          size="small"
          label={status.label}
          color={status.color}
          variant={status.filled ? 'filled' : 'outlined'}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
        {dcp?.encrypt && (() => {
          const total = recipient.kdms.length;
          const done = completedKdms.length;
          if (total === 0) {
            return <Chip size="small" label="No KDM" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
          }
          if (done === total) {
            const lbl = total > 1 ? `✓ ${done} KDMs` : '✓ KDM';
            return <Chip size="small" label={lbl} color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
          }
          return <Chip size="small" label={`✓ ${done}/${total} KDMs`} color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
        })()}
        {/* v10.8.1: plain-English chip naming the actual ops problem.
            Branches on which side of the window is wrong so the
            label tells the operator the failure mode, not our
            internal "mismatch" jargon. */}
        {(() => {
          const bad = (recipient.kdms || []).filter(
            (k) => k.window_covers_screening === false,
          );
          if (bad.length === 0) return null;
          // Split by failure mode: expires-too-soon vs activates-too-late.
          const expiresEarly = bad.filter((k) => {
            const screen = k.booking_first_screening ? new Date(k.booking_first_screening).getTime() : null;
            const validTo = k.valid_to ? new Date(k.valid_to).getTime() : null;
            return screen != null && validTo != null && validTo < screen;
          });
          const activatesLate = bad.filter((k) => {
            const screen = k.booking_first_screening ? new Date(k.booking_first_screening).getTime() : null;
            const validFrom = k.valid_from ? new Date(k.valid_from).getTime() : null;
            return screen != null && validFrom != null && validFrom > screen;
          });
          // Most operationally common case is expires-too-soon. Use
          // that copy unless every bad KDM is the activates-late case.
          let label;
          if (expiresEarly.length > 0) {
            label = bad.length === 1
              ? 'KDM expires before screening'
              : `${bad.length} KDMs expire before screening`;
          } else if (activatesLate.length > 0) {
            label = bad.length === 1
              ? 'KDM activates after screening'
              : `${bad.length} KDMs activate after screening`;
          } else {
            label = bad.length === 1 ? 'KDM window invalid' : `${bad.length} KDMs · window invalid`;
          }
          const tipLines = bad.map((k) => {
            const first = k.booking_first_screening
              ? new Date(k.booking_first_screening).toLocaleDateString()
              : '?';
            const to = k.valid_to ? new Date(k.valid_to).toLocaleDateString() : '?';
            const from = k.valid_from ? new Date(k.valid_from).toLocaleDateString() : '?';
            return `${k.screen_name || 'screen'} — valid ${from} → ${to}, screening ${first}`;
          });
          return (
            <Tooltip arrow title={
              <Box sx={{ fontSize: '0.72rem', whiteSpace: 'pre-line' }}>
                {`The key won't unlock the DCP at showtime:\n${tipLines.join('\n')}\n\nRe-issue with the canonical −14 days / +2 days window before the screening.`}
              </Box>
            }>
              <Chip
                size="small"
                label={label}
                color="error"
                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }}
              />
            </Tooltip>
          );
        })()}
        {kdmExpired && <Chip size="small" label="EXPIRED" color="error" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />}
        {!kdmExpired && kdmExpiringSoon && (
          <Chip size="small" label={`expires in ${Math.max(1, Math.round(hoursToExpiry))}h`} color="warning" sx={{ height: 20, fontSize: '0.6rem' }} />
        )}
        {/* v10.7.1: delivery-struggling indicator. Heuristic: recipient
            clicked download ≥2 times but never finished. Strong signal
            their network can't sustain a streaming pull of a multi-GB
            file. Operator should consider physical drive or a chunked
            downloader (cf. River Oaks vs Belcourt — same file, 31
            Mbps vs parallel 91-chunk pull). */}
        {(() => {
          const started = activeShare?.public_download_count || 0;
          const complete = activeShare?.confirmed_count || 0;
          const struggling = activeShare && started >= 2 && complete === 0;
          // v10.18.18: don't display "delivery struggling" once the
          // share is revoked (the digital attempt is over — the
          // chip would be lying about ongoing trouble) OR once a
          // physical delivery is in flight (the operator has
          // already pivoted — surfacing the old struggle is noise).
          const sharingDone = !activeShare || activeShare.revoked_at;
          const physicalInFlight = physicalDelivery
            && physicalDelivery.status !== 'cancelled'
            && physicalDelivery.status !== 'failed';
          if (!struggling || sharingDone || physicalInFlight) return null;
          return (
            <Tooltip
              arrow
              title={`Recipient has started ${started} downloads but none completed. Their network may not sustain a streaming pull of this file. Consider physical drive delivery.`}
            >
              <Chip
                size="small"
                label="delivery struggling"
                color="warning"
                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }}
              />
            </Tooltip>
          );
        })()}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={
          <Box sx={{ p: 0.5, fontSize: '0.75rem', lineHeight: 1.5 }}>
            <div><strong>{activeShare?.view_count || 0} viewed</strong> — opened the share landing page</div>
            <div><strong>{activeShare?.public_download_count || 0} started</strong> — clicked the Download button</div>
            <div><strong>{activeShare?.confirmed_count || 0} complete</strong> — finished downloading (full file served, verified)</div>
          </Box>
        }>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', cursor: 'help' }}>
            {activeShare ? `${activeShare.view_count || 0} v · ${activeShare.public_download_count || 0} s · ${activeShare.confirmed_count || 0} c` : 'no link'}
          </Typography>
        </Tooltip>
      </Box>

      {/* Expanded body — every detail the Library used to show. */}
      <Collapse in={expanded} unmountOnExit timeout="auto">
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
          {/* v10.21.50 Ship 1: theater details panel. Surfaces venue
              tech contact + per-screen projector/server/cert info
              inline so the operator can answer "is this the Dolby
              room with the old GDC server?" without leaving the
              Distribution page. Also renders a cert-expiry banner
              at the top when any screen's cert is past or within
              30 days. */}
          {recipient.venueId && (
            <TheaterDetailsPanel venueId={recipient.venueId} />
          )}

          {/* Venue history — what happened the last few times we
              delivered to this venue. Surfaces "last attempt: format
              unknown" right at the point of decision so the operator
              ships the right thing this time. Only renders when the
              card is bound to a venue (KDM-driven shares are; some
              press-screening shares have no venue). */}
          {recipient.venueId && outcomes !== null && outcomes.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.25 }}>
                Venue history
              </Typography>
              <Box sx={{ flex: 1 }}>
                {outcomes.slice(0, 3).map((o) => (
                  <Box key={o.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 80 }}>
                      {new Date(o.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 500 }}>
                      .{o.format_used || '?'}
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontSize: '0.72rem',
                      color: o.outcome === 'success_clean' ? 'success.main'
                        : ['format_unknown','download_failed','kdm_filter_blocked','av_issue'].includes(o.outcome) ? 'error.main'
                        : 'warning.main',
                    }}>
                      → {o.outcome.replace(/_/g, ' ')}
                    </Typography>
                    {o.notes && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        — {o.notes.slice(0, 100)}{o.notes.length > 100 ? '…' : ''}
                      </Typography>
                    )}
                  </Box>
                ))}
                {outcomes.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    +{outcomes.length - 3} earlier outcome{outcomes.length - 3 === 1 ? '' : 's'} — full history on the venue page
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* KDM section (encrypted DCPs only) */}
          {dcp?.encrypt && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.25 }}>
                Unlock Key
              </Typography>
              <Box sx={{ flex: 1 }}>
                {haveKdm ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label="✓ Issued" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    {completedKdm.screen_name && (
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{completedKdm.screen_name}</Typography>
                    )}
                    {completedKdm.valid_from && completedKdm.valid_to && (
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        · valid {fmtDate(completedKdm.valid_from)} → {fmtDate(completedKdm.valid_to)}
                      </Typography>
                    )}
                    {kdmExpired && <Chip size="small" label="EXPIRED" color="error" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />}
                    {!kdmExpired && kdmExpiringSoon && (
                      <Chip size="small" label={`expires in ${Math.max(1, Math.round(hoursToExpiry))}h`} color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                    {sharedCertOthers && (
                      <Tooltip arrow title={`Same projector certificate as: ${sharedCertOthers}. Verify each screen has its own unique cert.`}>
                        <Chip size="small" label="shared cert" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                      </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Copy KDM share link (public, safe to send to venue)">
                      <IconButton size="small" onClick={async (e) => {
                        e.stopPropagation();
                        // Get a stable, non-expiring public share URL for
                        // this KDM record. Avoids the old broken pattern
                        // of pasting an operator-JWT URL into venue email
                        // (JWTs expire in 30 min — link dies before the
                        // venue even opens the message).
                        try {
                          const r = await api.get(`/api/kdm/records/${completedKdm.id}/share-link`);
                          await navigator.clipboard.writeText(r.data.url);
                          onCopy('KDM share link copied');
                        } catch (err) {
                          // Backend may not yet have the share-link endpoint
                          // (deployed asynchronously while a DCP packaging is
                          // in flight). Surface a clear message rather than
                          // falling back to the broken JWT-URL pattern.
                          if (err.response?.status === 404) {
                            onCopy('Share-link endpoint not yet deployed — try again in a few minutes', 'error');
                          } else {
                            onCopy('Could not get share link', 'error');
                          }
                        }
                      }}>
                        <CopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download KDM bundle (zip with KDM XML + QC)">
                      <IconButton size="small" onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const resp = await api.get(`/api/kdm/records/${completedKdm.id}/download-bundle`, { responseType: 'blob' });
                          const url = URL.createObjectURL(resp.data);
                          const a = document.createElement('a'); a.href = url;
                          a.download = resp.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `kdm_${completedKdm.id}.zip`;
                          a.click(); URL.revokeObjectURL(url);
                        } catch (_) { onCopy('KDM download failed', 'error'); }
                      }}>
                        <PackageIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Email KDM bundle to venue tech contact">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEmailKdm(completedKdm); }}>
                        <ResendIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete KDM record">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeleteKdm(completedKdm); }}>
                        <DeleteIcon sx={{ fontSize: 14 }} color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">Not issued</Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Delivery section — Digital + Physical line items, each
              with its own status chip + details. v10.18.19: pre-fix
              DELIVERY only showed the digital share, which meant a
              pivoted-to-physical card (Harkins) was missing the
              tracking number / lifecycle for the actual delivery
              method while showing "Revoked" for a path that nobody
              cared about anymore. Both line items now sit under a
              single DELIVERY label so the operator sees the full
              fulfillment picture in one place. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.25 }}>
              Delivery
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* DIGITAL line item — existing share UI, now prefixed
                  with a small label so it pairs visually with the
                  Physical line below. v10.18.24: order=2 floats Digital
                  below Physical when the drive actually delivered. */}
              <Box sx={{ order: physicalFirst ? 2 : 1 }}>
              {activeShare ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', minWidth: 56 }}>
                      Digital
                    </Typography>
                    <Chip
                      size="small"
                      label={activeShare.revoked_at ? 'Revoked' : (activeShare.email_sent_at ? '✓ Emailed' : 'Created')}
                      color={activeShare.revoked_at ? 'default' : (activeShare.email_sent_at ? 'success' : 'info')}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {downloadConfirmed && (
                      <Chip size="small" label="✓ Downloaded" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {activeShare.email_sent_at
                        ? `sent ${fmtDateTime(activeShare.email_sent_at)}`
                        : (activeShare.created_at ? `created ${fmtDateTime(activeShare.created_at)}` : '—')}
                      {' · '}
                      {/* v10.21.50: clickable expiry. Was static text
                          before; operator had to use the API directly.
                          The editor in textOnly mode preserves the
                          inline-with-everything-else density while
                          adding the affordance. */}
                      <ShareExpiryEditor
                        share={activeShare}
                        textOnly
                        onChanged={() => {
                          // Re-fetch packages via the existing
                          // shareRevoked event hook — same pattern
                          // RecordOutcomeDialog uses on save. The
                          // parent listener calls fetchPackages.
                          window.dispatchEvent(new Event('screeners:shareRevoked'));
                        }}
                      />
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {!activeShare.revoked_at && (
                      <>
                        <Tooltip title="Copy delivery link">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(`Link copied: /d/${activeShare.token}`); navigator.clipboard.writeText(`${CANONICAL_ORIGIN}/d/${activeShare.token}`); }}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open the recipient's landing page in a new tab">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(`${CANONICAL_ORIGIN}/d/${activeShare.token}`, '_blank'); }}>
                            <OpenIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Compose follow-up email (delivery confirmation, ingest help, etc.) — pre-filled with checksum, KDM info, mix notes">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onComposeEmail({
                                touchpoint: 'dcp_delivery',
                                subjectId: activeShare.id,
                                contextLabel: `${recipient.label} · ${dcp?.name || ''}`,
                              });
                            }}
                          >
                            <EmailIcon sx={{ fontSize: 14 }} color="primary" />
                          </IconButton>
                        </Tooltip>
                        {/* Record outcome — re-wired in v10.17.17.
                            Opens the dialog that lets the operator log
                            "tested successfully" (pre-show) or "screening
                            successful" (post-show) as distinct events,
                            plus the legacy generic-success / failure
                            codes. */}
                        {recipient.venueId && (
                          <Tooltip title="Record what happened (test pass / screening successful / problem)">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setOutcomeDialogOpen(true); }}
                            >
                              <OutcomeIcon sx={{ fontSize: 14 }} color="success" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Re-send delivery email">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onResend(activeShare.id); }}>
                            <ResendIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke this delivery link">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRevoke(activeShare.id, recipient.label); }}>
                            <RevokeIcon sx={{ fontSize: 14 }} color="error" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>

                  {/* Per-attempt rollup. ONE row per client IP — that's
                      one delivery attempt regardless of how many parallel
                      HTTP 206 byte-range requests the recipient's
                      downloader fired. Status answers the operator's
                      actual question: "did this venue get the file?"
                      The compact `v · s · c` counter on the collapsed
                      row already surfaces views/starts/completes; we
                      don't repeat them here in expanded view (used to
                      print the same numbers in a verbose form). */}
                  {tfs === undefined ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                      Loading transfer telemetry…
                    </Typography>
                  ) : (tfs.sessions || []).length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                      No download attempts recorded yet.
                    </Typography>
                  ) : (() => {
                    const sessions = tfs.sessions || [];
                    const fileSize = tfs.file_size;
                    // `likely_complete` counts toward "delivered" in the
                    // summary — operator sees the strong signal up front
                    // rather than having to expand the rollup. Scanner
                    // probes (Outlook Safe Links / Defender / Mimecast
                    // URL Defense) are excluded from the delivery-attempt
                    // tally entirely — they're not human downloads.
                    const completed = sessions.filter((s) => s.status === 'complete' || s.status === 'likely_complete').length;
                    const inFlight = sessions.filter((s) => s.status === 'in_progress').length;
                    const scanners = sessions.filter((s) => s.status === 'scanner_probe').length;
                    const realAttempts = sessions.length - scanners;
                    return (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary' }}>
                          {realAttempts} delivery attempt{realAttempts === 1 ? '' : 's'} ·{' '}
                          <Box component="span" sx={{ color: completed > 0 ? 'success.main' : 'text.secondary', fontWeight: completed > 0 ? 600 : 'inherit' }}>
                            {completed} delivered
                          </Box>
                          {inFlight > 0 && (
                            <> · <Box component="span" sx={{ color: 'info.main', fontWeight: 600 }}>{inFlight} in flight</Box></>
                          )}
                          {scanners > 0 && (
                            <Tooltip title="Email security scanners (Outlook Safe Links, Defender SmartScreen, Proofpoint URL Defense) fan a URL across a proxy farm to sniff the first few MB for malware. Not a human download attempt.">
                              <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
                                {' · '}{scanners} URL scan{scanners === 1 ? '' : 's'}
                              </Box>
                            </Tooltip>
                          )}
                          {fileSize && <> · file {fmtBytes(fileSize)}</>}
                        </Typography>
                        <Box sx={{ maxHeight: 280, overflow: 'auto', mt: 0.5 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ '& th': { py: 0.25, fontSize: '0.65rem', textTransform: 'none', letterSpacing: 0 } }}>
                                <TableCell>Status</TableCell>
                                <TableCell>Client IP</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell align="right">Coverage</TableCell>
                                <TableCell align="right">Peak</TableCell>
                                <TableCell align="right">Now</TableCell>
                                <TableCell align="right">Duration</TableCell>
                                <TableCell align="right">Parallel transfers</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sessions.map((s) => {
                                // Two new bands beyond complete/in_progress/stalled/abandoned:
                                //   likely_complete — high coverage + long duration + sustained
                                //     connection + good peak bitrate. The math says "they
                                //     almost certainly got it" even when bytes_sent says short.
                                //   questionable — real attempt but ambiguous: needs venue
                                //     confirmation rather than being written off as abandoned.
                                const statusColor =
                                  s.status === 'complete' ? 'success' :
                                  s.status === 'likely_complete' ? 'success' :
                                  s.status === 'in_progress' ? 'info' :
                                  s.status === 'questionable' ? 'warning' :
                                  s.status === 'stalled' ? 'warning' :
                                  s.status === 'scanner_probe' ? 'default' : 'error';
                                const statusLabel =
                                  s.status === 'complete' ? '✓ complete' :
                                  s.status === 'likely_complete' ? '≈ likely complete' :
                                  s.status === 'in_progress' ? `◉ active (${s.active_count})` :
                                  s.status === 'questionable' ? '? questionable' :
                                  s.status === 'stalled' ? 'stalled' :
                                  s.status === 'scanner_probe' ? 'URL scan' : 'abandoned';
                                const statusTooltip =
                                  s.status === 'likely_complete'
                                    ? `${s.coverage_percent.toFixed(1)}% covered over ${(s.duration_seconds/60).toFixed(0)} min with a consistent connection (${(s.activity_ratio*100).toFixed(0)}% active time, peak ${s.peak_mbps?.toFixed(0)} Mbps). Bytes counter likely undercounts.`
                                  : s.status === 'questionable'
                                    ? `${s.coverage_percent.toFixed(1)}% covered, ${(s.activity_ratio*100).toFixed(0)}% active time — operator should confirm with venue.`
                                  : s.status === 'scanner_probe'
                                    ? 'Email security scanner (Outlook Safe Links / Defender / Mimecast) — fingerprint: single sub-5s GET pulling a small header sample. Not a human download attempt.'
                                  : '';
                                return (
                                  <TableRow key={s.client_ip || 'unknown'} sx={{ '& td': { py: 0.5, fontSize: '0.7rem' } }}>
                                    <TableCell>
                                      <Tooltip title={statusTooltip} placement="top" arrow>
                                        <Chip
                                          size="small"
                                          label={statusLabel}
                                          color={statusColor}
                                          variant={s.status === 'complete' || s.status === 'likely_complete' ? 'filled' : 'outlined'}
                                          sx={{ fontSize: '0.62rem', height: 18, fontWeight: 600 }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{s.client_ip || '—'}</TableCell>
                                    <TableCell>{s.started_at ? fmtDateTime(s.started_at) : '—'}</TableCell>
                                    <TableCell align="right">
                                      <span>{fmtBytes(s.covered_bytes)}</span>
                                      <span style={{ color: 'rgba(232,230,227,0.45)', marginLeft: 6 }}>
                                        {s.coverage_percent.toFixed(1)}%
                                      </span>
                                    </TableCell>
                                    <TableCell align="right">{s.peak_mbps ? `${s.peak_mbps.toFixed(0)} Mbps` : '—'}</TableCell>
                                    <TableCell align="right">{s.current_mbps ? `${s.current_mbps.toFixed(0)} Mbps` : '—'}</TableCell>
                                    <TableCell align="right">{fmtDur(s.duration_seconds)}</TableCell>
                                    <TableCell align="right">
                                      <Tooltip title={`${s.request_count} parallel transfers fired by this client (HTTP byte-range requests opened concurrently to fetch the file)${s.total_bytes_sent !== s.covered_bytes ? ` · ${fmtBytes(s.total_bytes_sent)} total bytes shipped, including retried chunks` : ''}`} arrow>
                                        <span>{s.request_count}</span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    );
                  })()}
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', minWidth: 56 }}>
                    Digital
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    No delivery link created.
                  </Typography>
                </Box>
              )}
              </Box>

              {/* PHYSICAL line item — renders when a non-cancelled
                  physical_delivery row exists for this venue+DCP.
                  Same visual cadence as the digital row: label +
                  status chip + inline details + follow-on chips
                  (Received / Tested) when those events have fired.
                  v10.18.24: order=1 floats Physical above Digital when
                  the drive actually delivered. */}
              <Box sx={{ order: physicalFirst ? 1 : 2 }}>
              {physicalDelivery && physicalDelivery.status !== 'cancelled' && (() => {
                const pd = physicalDelivery;
                const carrier = pd.carrier || (pd.tracking_number ? 'FedEx' : null);
                const ct = [carrier, pd.tracking_number].filter(Boolean).join(' ');
                // v12.0.0: shared wording (utils/physicalDelivery).
                const pdStatus = physicalStatus(pd);
                const chipColor = pdStatus.color;
                const chipLabel = pd.status === 'staged'
                  ? `Ready to ship (${driveContents(pd)})`
                  : pdStatus.label;
                // v10.18.20: surface Mark received / Mark tested on
                // shipped+ rows so the operator doesn't have to leave
                // the distribution view to log these milestones. The
                // chip flips to ✓ Received / ✓ Tested once saved.
                const canMarkReceived = !pd.received_at
                  && ['shipped', 'delivered', 'returned'].includes(pd.status);
                const canMarkTested = !pd.tested_at
                  && ['shipped', 'delivered', 'returned'].includes(pd.status);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', minWidth: 56 }}>
                      Physical
                    </Typography>
                    <Chip
                      size="small"
                      label={chipLabel}
                      color={chipColor}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {pd.received_at && (
                      <Tooltip title={`${pd.received_by_name || ''} · ${new Date(pd.received_at).toLocaleString()} (click to edit)`}>
                        <Chip
                          size="small" label="✓ Received" color="success"
                          sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                          onClick={() => setMarkKind('received')}
                        />
                      </Tooltip>
                    )}
                    {pd.tested_at && (
                      <Tooltip title={
                        `${pd.tested_by_name || ''} · ${new Date(pd.tested_at).toLocaleString()} (click to edit)` +
                        (pd.test_notes ? `\n${pd.test_notes}` : '')
                      }>
                        <Chip
                          size="small" label="✓ Tested" color="success"
                          sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                          onClick={() => setMarkKind('tested')}
                        />
                      </Tooltip>
                    )}
                    {ct && (
                      <Tooltip title={`Click to copy · ${ct}`}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem', color: 'text.secondary', cursor: 'pointer' }}
                          onClick={() => pd.tracking_number && navigator.clipboard?.writeText(pd.tracking_number)}
                        >
                          {ct}
                        </Typography>
                      </Tooltip>
                    )}
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {pd.delivered_at
                        ? `delivered ${fmtDateTime(pd.delivered_at)}`
                        : (pd.shipped_at
                          ? `shipped ${fmtDateTime(pd.shipped_at)}`
                          : (pd.staged_at ? `staged ${fmtDateTime(pd.staged_at)}` : ''))}
                      {pd.signed_for_by_name ? ` · signed by ${pd.signed_for_by_name}` : ''}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {canMarkReceived && (
                      <Button
                        size="small" variant="text"
                        sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5, py: 0 }}
                        onClick={() => setMarkKind('received')}
                      >
                        Mark received
                      </Button>
                    )}
                    {canMarkTested && (
                      <Button
                        size="small" variant="text"
                        sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5, py: 0 }}
                        onClick={() => setMarkKind('tested')}
                      >
                        Mark tested
                      </Button>
                    )}
                  </Box>
                );
              })()}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
    {recipient.venueId && (
      <RecordOutcomeDialog
        open={outcomeDialogOpen}
        onClose={() => setOutcomeDialogOpen(false)}
        venueId={recipient.venueId}
        venueLabel={recipient.label}
        dcpJobId={dcp?.id}
        shareId={activeShare?.id}
        // v10.17.19: lets the dialog surface the "also revoke" option
        // only when there's a live share to revoke. Revoked / missing
        // shares hide the affordance.
        shareIsActive={!!activeShare && !activeShare.revoked_at}
        kdmRecordId={completedKdm?.id}
        onSaved={(meta) => {
          // Bump the refresh counter so the outcomes timeline pulls
          // the new row immediately.
          setOutcomesRefresh((n) => n + 1);
          // If the operator also revoked the share, refresh packages
          // so the recipient card's status chip flips to "Revoked"
          // without a manual page reload.
          if (meta?.revokedShare) {
            try {
              window.dispatchEvent(new CustomEvent('screeners:shareRevoked'));
            } catch (_e) { /* non-fatal */ }
          }
          try {
            window.dispatchEvent(new CustomEvent('screeners:outcomeSaved'));
          } catch (_e) { /* non-fatal */ }
        }}
      />
    )}

    {/* v10.18.20: Mark received / Mark tested via the shared
        PhysicalDeliveryMarkDialog (the same component the operator
        panel uses for Mark delivered). DRY across the two surfaces;
        the dialog handles its own form state, busy state, and
        error display. */}
    <PhysicalDeliveryMarkDialog
      open={markKind !== null}
      onClose={() => setMarkKind(null)}
      kind={markKind}
      delivery={physicalDelivery
        ? { ...physicalDelivery, venue_name: recipient.label }
        : null}
      onSaved={() => {
        try {
          window.dispatchEvent(new CustomEvent('screeners:physicalUpdated'));
        } catch (_e) { /* non-fatal */ }
      }}
    />
    </>
  );
}

// ── main page ──

export default function DCPDistribution() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { jobs: dcpJobs } = useSelector((s) => s.dcpJobs);
  const { packages } = useSelector((s) => s.delivery);
  const userInfo = useSelector((s) => s.auth.userInfo);
  const { records: kdmRecords } = useSelector((s) => s.kdm);
  // Bookings drive the card's leading date. Pre-v10.4.49 we showed the
  // KDM valid_from (= screening − 14 d), which is correct as a key
  // validity start but reads as the wrong date to operators who think
  // in terms of when the screening actually IS.
  const { bookings } = useSelector((s) => s.bookings);

  const [tab, setTab] = useState(searchParams.get('tab') === 'kdms' ? 'kdms' : 'distributions');
  const [filter, setFilter] = useState('');
  // Auto-hide cards whose last screening day has already passed.
  // Toggleable so the operator can still audit yesterday's deliveries.
  const [showPast, setShowPast] = useState(false);
  // v10.7.2: hide older-DCP cards when a newer DCP exists for the same
  // recipient. Re-cuts shouldn't bury the active delivery in clutter;
  // the toggle reveals them when ops needs to audit/revoke the old ones.
  const [showSuperseded, setShowSuperseded] = useState(false);
  // KDM-tab status filter: '' / active / expiring / expired / pending / revoked.
  // Driven by both the dropdown and the click-to-filter aggregate counters.
  const [kdmStatusFilter, setKdmStatusFilter] = useState('');
  const [pickedDCP, setPickedDCP] = useState(null);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteKdmConfirm, setDeleteKdmConfirm] = useState(null);
  const [kdmEmailDialog, setKdmEmailDialog] = useState(null);
  const [kdmEmailForm, setKdmEmailForm] = useState({ cc: '', message: '' });
  const [transfersByShare, setTransfersByShare] = useState({});
  // v10.18.18: physical-delivery rows for the visible cards. Keyed
  // by `${dcp_job_id}:${venue_id}` for O(1) lookup at render time.
  // Surfaces the Harkins-style "pivoted to physical, drive shipped"
  // case on each RecipientCard without requiring the operator to
  // manually log a pivot outcome.
  const [physicalDeliveries, setPhysicalDeliveries] = useState([]);
  // v10.18.27: company-wide rollup of past KDM email events from the
  // audit log, grouped by venue. Surfaces deliveries to venues whose
  // KDM rows got hard-deleted with a cancelled parent DCP (Laemmle /
  // Landmark April-2026 sends are the canonical example). Rendered as
  // a separate "Historical deliveries to other venues" section at the
  // bottom of the distributions tab.
  const [pastDeliveriesByVenue, setPastDeliveriesByVenue] = useState([]);
  // v11.0.0 — latest delivery outcome per (venue_id, dcp_job_id) for
  // the workspace. Used by the recipientCards builder to mark a card
  // past-screening once the operator records a terminal outcome
  // (screening_successful, tested_successfully, pivoted, cancelled).
  const [outcomesSummary, setOutcomesSummary] = useState([]);
  // v10.19.1: venue catalog used to attribute share-only recipient
  // buckets back to a venue by tech_contact_email match. Without this,
  // a share whose KDM has been deleted (or that was sent before the
  // KDM/booking existed) renders as just "Rob Saucedo" with no
  // theater name — disconnected from the active Star Cinema Grill
  // card that has the same email.
  const [venuesForEmailLookup, setVenuesForEmailLookup] = useState([]);
  // Email composer state — set to { touchpoint, subjectId, contextLabel }
  // when a recipient card asks to compose; null = closed.
  const [composer, setComposer] = useState(null);

  // Bulk ops state — same threshold-gated pattern as Library pages.
  const [bulkForce, setBulkForce] = useState(isDistributionBulkOpsForced());
  const [bulkAction, setBulkAction] = useState(null);
  const toggleBulkForce = () => {
    const next = !bulkForce;
    setBulkForce(next);
    setDistributionBulkOpsForced(next);
  };
  const bulk = useBulkRunner({
    onComplete: ({ failed, total }) => {
      dispatch(fetchKDMRecords({}));
      if (failed > 0) {
        setSnackbar({ open: true, message: `${failed} of ${total} bulk operations failed.`, severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: `${total} KDM${total === 1 ? '' : 's'} processed`, severity: 'success' });
      }
    },
  });
  const selectedKdmIds = bulk.selectedIds;

  // Cheap, idempotent. Repeated calls for the same shareId don't re-fetch.
  const fetchTransfersFor = useMemo(() => async (shareId) => {
    let alreadyHave = false;
    setTransfersByShare((prev) => {
      if (prev[shareId]) alreadyHave = true;
      return prev;
    });
    if (alreadyHave) return;
    try {
      // v10.4.46: switched from per-request `/transfers` to the
      // aggregated `/transfer-sessions`. The old flat list rendered
      // 91 rows for a single venue's parallel-resumable downloader;
      // sessions answers "did the file get there?" with one row per
      // attempt + an expand-for-detail affordance.
      const { data } = await api.get(`/api/delivery/shares/${shareId}/transfer-sessions`);
      setTransfersByShare((prev) => ({
        ...prev,
        [shareId]: data || { sessions: [], file_size: null },
      }));
    } catch (_) {
      setTransfersByShare((prev) => ({
        ...prev,
        [shareId]: { sessions: [], file_size: null },
      }));
    }
  }, []);

  // Initial fetch + 5s polling so newly-created KDMs / shares appear
  // without a manual refresh.
  useEffect(() => {
    dispatch(fetchDCPJobs({}));
    dispatch(fetchPackages({ sourceType: 'dcp' }));
    dispatch(fetchKDMRecords({}));
    dispatch(fetchBookings({}));
    // v10.18.18: physical-delivery rows alongside the digital
    // shares. Same 15s cadence — drive status changes slowly
    // (stage → ship → deliver → receive → test), no need for the
    // 5s polling the dedicated /dcp/:id/distribute page uses.
    let cancelledPhys = false;
    const loadPhys = async () => {
      try {
        const { data } = await api.get('/api/physical-delivery/deliveries');
        if (!cancelledPhys) setPhysicalDeliveries(data || []);
      } catch (_e) { /* page works without this */ }
    };
    loadPhys();
    // v10.18.27: past KDM sends from the audit log, grouped by venue.
    // One-shot pull (audit-log entries are immutable historical records,
    // no need to poll). Failure is non-fatal — the page works without it.
    api.get('/api/venues/past-deliveries-summary')
      .then((r) => setPastDeliveriesByVenue(r.data || []))
      .catch(() => setPastDeliveriesByVenue([]));
    // v11.0.0 — workspace-wide latest outcomes per (venue, dcp). The
    // past-screening filter uses these so a card whose operator has
    // already recorded `screening_successful` / similar disappears
    // from the live Distribution view even if its KDM windows
    // happen to span future dates (re-issued, residency overrun, etc).
    api.get('/api/venues/outcomes/summary')
      .then((r) => setOutcomesSummary(r.data || []))
      .catch(() => setOutcomesSummary([]));
    // v10.19.1: venue catalog for email-based recipient attribution.
    api.get('/api/venues/')
      .then((r) => setVenuesForEmailLookup(r.data || []))
      .catch(() => setVenuesForEmailLookup([]));
    // Polling at 15s — KDM data changes slowly (issued, downloaded,
    // expired). The previous 5s interval was wasteful; bumped to
    // match the rest of the app's "background" refresh cadence.
    const t = setInterval(() => {
      dispatch(fetchPackages({ sourceType: 'dcp' }));
      dispatch(fetchKDMRecords({}));
      loadPhys();
    }, 15000);
    // v10.17.19: pivot-to-physical revokes the share from inside
    // RecordOutcomeDialog. Listen for the custom event so the
    // recipient card's status chip flips to "Revoked / pivoted" the
    // moment the operator clicks Save, rather than waiting up to 15s
    // for the next poll tick.
    const onShareRevoked = () => {
      dispatch(fetchPackages({ sourceType: 'dcp' }));
      loadPhys();
    };
    const onPhysicalUpdated = () => {
      // v10.18.20: mark-received / mark-tested fires this from the
      // inline RecipientCard dialog so the chip flips to ✓ Received
      // / ✓ Tested without waiting for the next 15s tick.
      loadPhys();
    };
    window.addEventListener('screeners:shareRevoked', onShareRevoked);
    window.addEventListener('screeners:physicalUpdated', onPhysicalUpdated);
    return () => {
      cancelledPhys = true;
      clearInterval(t);
      window.removeEventListener('screeners:shareRevoked', onShareRevoked);
      window.removeEventListener('screeners:physicalUpdated', onPhysicalUpdated);
    };
  }, [dispatch]);

  // Lookup map keyed by `${dcp_job_id}:${venue_id}` so RecipientCard
  // can pull its delivery in O(1). Most recent (by created_at) wins
  // when a venue has multiple drives for the same DCP — rare but
  // happens when a drive is lost in transit and re-staged.
  const physicalDeliveryByKey = React.useMemo(() => {
    const m = new Map();
    for (const d of physicalDeliveries) {
      if (!d.venue_id || !d.dcp_job_id) continue;
      const key = `${d.dcp_job_id}:${d.venue_id}`;
      const existing = m.get(key);
      if (!existing || new Date(d.created_at || 0) > new Date(existing.created_at || 0)) {
        m.set(key, d);
      }
    }
    return m;
  }, [physicalDeliveries]);

  // ?dcp=N pre-fills the DCP picker (Library deep-links here with this
  // param). Once-per-id guard so the operator closing the dialog and
  // clicking back doesn't immediately re-open it.
  const [autoOpenedFor, setAutoOpenedFor] = useState(null);
  useEffect(() => {
    const id = parseInt(searchParams.get('dcp') || '', 10);
    if (!id || dcpJobs.length === 0) return;
    const job = dcpJobs.find((j) => j.id === id);
    if (!job) return;
    if (!pickedDCP) setPickedDCP(job);
    // Auto-open the Distribute dialog the first time this id arrives —
    // saves the operator a click on the most common entry path
    // (Awaiting-KDM stat in DCP Library deep-links here). The
    // `autoOpenedFor` guard ensures we don't re-open when the operator
    // closes the dialog without distributing.
    if (autoOpenedFor !== id) {
      setAutoOpenedFor(id);
      setDistributeOpen(true);
    }
  }, [searchParams, dcpJobs, pickedDCP, autoOpenedFor]);

  const completedDCPs = useMemo(
    () => dcpJobs.filter((j) => j.status === 'completed' && !j.deleted_at),
    [dcpJobs],
  );

  // Next upcoming screening date+time per DCP, derived from the
  // earliest non-past KDM valid_from. Surfaces the "show date/time"
  // alongside the DCP name in the picker so the operator distinguishes
  // multiple DCPs of the same title (e.g. SDR + HDR cuts) by their
  // upcoming run, not just the encode timestamp.
  const nextScreeningByDcp = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startMs = startOfToday.getTime();
    const acc = {};
    for (const k of (kdmRecords || [])) {
      if (!k.valid_from || !k.dcp_job_id) continue;
      const ts = new Date(k.valid_from).getTime();
      const cutoff = k.valid_to ? new Date(k.valid_to).getTime() : ts;
      if (cutoff < startMs) continue; // past run — don't surface
      const cur = acc[k.dcp_job_id];
      if (cur == null || ts < cur) acc[k.dcp_job_id] = ts;
    }
    return acc;
  }, [kdmRecords]);

  const fmtShowDateTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${time}`;
  };

  // Build per-(DCP, recipient) cards. Bucket KDMs + Shares of the same
  // DCP by lowercased email-or-venue so KDM + Share for the same venue
  // collapse into ONE card (the inverse of the per-share table — that
  // showed two rows for an encrypted DCP delivery).
  const recipientCards = useMemo(() => {
    const dcpById = Object.fromEntries(dcpJobs.map((j) => [j.id, j]));
    // v10.19.1: email → venue resolver. Maps any known recipient email
    // back to the venue catalog so share-only buckets (no KDM, no
    // booking) still surface with the theater name + chain. Without
    // this, a recipient like rsaucedo@theriveroakstheatre.com whose
    // KDM is on a different DCP renders as a bare "Rob Saucedo" card
    // disconnected from their Star Cinema Grill / River Oaks card.
    const emailToVenue = (() => {
      const m = new Map();
      for (const v of (venuesForEmailLookup || [])) {
        if (v.tech_contact_email) {
          const key = v.tech_contact_email.trim().toLowerCase();
          if (key && !m.has(key)) {
            m.set(key, { id: v.id, name: v.name, chain: v.chain });
          }
        }
      }
      return m;
    })();
    // Bucket: { [dcp_id]: Map(key → { label, email, kdms[], shares[] }) }.
    // v10.6.1: bucketing key now includes screen_id when available, so
    // a venue with TWO auditoriums (Aviation Cinemas Texas Theater
    // Upstairs + Downstairs, each with their own KDM) gets TWO rows
    // instead of merging into one. Pre-v10.6.1 the second KDM was
    // invisible — operator-reported "I sent 2 KDMs but only see one
    // Unlock Key."
    const byDcp = new Map();
    // v10.7.3: ONE card per (DCP, recipient_email). The DCP is a
    // single file delivered to the venue once — many KDMs (one per
    // auditorium) can target the same download. Pre-v10.7.3 we
    // bucketed by screen_id which produced TWO cards for Aviation
    // Cinemas Texas (Upstairs + Downstairs) and looked like we were
    // delivering the file twice. Now: one card, KDMs list ALL screens
    // serviced by this delivery.
    const _key = (email, name) => (email || name || '').trim().toLowerCase() || '__';
    const ensure = ({ dcpId, email, name }) => {
      if (!byDcp.has(dcpId)) byDcp.set(dcpId, new Map());
      const m = byDcp.get(dcpId);
      const k = _key(email, name) || `__row_${m.size}`;
      if (!m.has(k)) m.set(k, { label: '', email: '', kdms: [], shares: [] });
      const row = m.get(k);
      row.label = row.label || name || email || '—';
      row.email = row.email || email || '';
      // v10.19.1: if this bucket came from a share (no venue context)
      // and the email matches a venue's tech contact, attribute it
      // back to the venue. The KDM path already sets venueId directly
      // — this is the fallback for share-only entries that pre-date
      // (or post-date) the KDM cycle.
      if (!row.venueId && email) {
        const v = emailToVenue.get(email.trim().toLowerCase());
        if (v) {
          row.venueId = v.id;
          // Replace the bare recipient-name label with the venue name
          // (chain-prefixed to match the KDM-derived label format —
          // see backend kdm.py:376 venue_display). So a share-only
          // card to rsaucedo@theriveroakstheatre.com now reads
          // "Star Cinema Grill River Oaks Theatre" and visually
          // pairs with its KDM-derived sibling card.
          row.label = v.chain ? `${v.chain} ${v.name}` : v.name;
          row.chain = v.chain || row.chain;
        }
      }
      return row;
    };
    for (const k of (kdmRecords || [])) {
      const row = ensure({
        dcpId: k.dcp_job_id,
        email: k.tech_contact_email,
        name: k.venue_name,
      });
      row.kdms.push(k);
      if (k.venue_id && !row.venueId) row.venueId = k.venue_id;
    }
    for (const pkg of (packages || [])) {
      if (pkg.source_type !== 'dcp' || !pkg.source_job_id) continue;
      for (const s of (pkg.shares || [])) {
        ensure({
          dcpId: pkg.source_job_id,
          email: s.recipient_email,
          name: s.recipient_name,
        }).shares.push(s);
      }
    }
    // Flatten to a per-card list with the matching DCP attached.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTodayMs = startOfToday.getTime();
    const out = [];
    for (const [dcpId, m] of byDcp.entries()) {
      const dcp = dcpById[dcpId];
      const cards = Array.from(m.values()).map((r) => {
        const lastTs = Math.max(
          ...r.kdms.map((k) => new Date(k.created_at || 0).getTime()),
          ...r.shares.map((s) => new Date(s.created_at || 0).getTime()),
          0,
        );
        // Screening date — resolution hierarchy as of v10.17.18:
        //
        //   1. Each KDM row's `booking_first_screening` field. The
        //      backend resolves this from bookings first, then form_tokens
        //      (see services/kdm_window_policy.screening_dates_for).
        //      Both sources are the actual screening date the operator
        //      committed; KDM valid_from is NOT (it's screening - 14d).
        //   2. Bookings Redux slice — the older fallback for cards that
        //      somehow lack a KDM (or whose KDM predates the backend
        //      enrichment). Same query as before.
        //   3. Last resort: KDM valid_from. This is the wrong date to
        //      anchor a card with — it's two weeks before the screening
        //      — but we render SOMETHING so the card isn't completely
        //      undated when no booking and no form_token exist.
        //
        // This bug has come back several times because the fallback
        // path at step 3 silently kicks in whenever the operator hasn't
        // created a calendar entry yet (which is most of the time
        // before screening day). The new step 1 reads the screening
        // date the form_token already captured at invite time, so the
        // card stays accurate without needing a booking row to exist.
        const kdmBookingFirsts = r.kdms
          .map((k) => k.booking_first_screening && new Date(k.booking_first_screening).getTime())
          .filter(Boolean);
        const kdmBookingLasts = r.kdms
          .map((k) => k.booking_last_screening && new Date(k.booking_last_screening).getTime())
          .filter(Boolean);
        const venueIds = [...new Set(r.kdms.map((k) => k.venue_id).filter(Boolean))];
        const matchingBookings = (bookings || []).filter((b) =>
          b.dcp_job_id === dcpId && venueIds.includes(b.venue_id) && b.status !== 'cancelled'
        );
        const bookingFirsts = matchingBookings
          .map((b) => b.first_screening && new Date(b.first_screening).getTime())
          .filter(Boolean);
        const bookingLasts = matchingBookings
          .map((b) => b.last_screening && new Date(b.last_screening).getTime())
          .filter(Boolean);
        const validFroms = r.kdms.map((k) => k.valid_from && new Date(k.valid_from).getTime()).filter(Boolean);
        const validTos   = r.kdms.map((k) => k.valid_to   && new Date(k.valid_to).getTime()).filter(Boolean);
        const nextScreeningTs = kdmBookingFirsts.length
          ? Math.min(...kdmBookingFirsts)
          : (bookingFirsts.length
              ? Math.min(...bookingFirsts)
              : (validFroms.length ? Math.min(...validFroms) : null));
        const lastWindowTs = kdmBookingLasts.length
          ? Math.max(...kdmBookingLasts)
          : (bookingLasts.length
              ? Math.max(...bookingLasts)
              : (validTos.length ? Math.max(...validTos) : null));
        // v11.0.0 — a card is past-screening when EITHER its computed
        // window is in the past OR the operator has recorded a
        // terminal outcome (screening_successful, tested_successfully,
        // pivoted_to_alt_delivery, cancelled) for any of its venues +
        // this DCP. The outcomes signal is what fixes the Burns Court
        // pattern: re-issued KDMs whose windows happen to span future
        // dates even though the actual screening already happened.
        const TERMINAL_OUTCOMES = new Set([
          'screening_successful',
          'tested_successfully',
          'pivoted_to_alt_delivery',
          'cancelled',
        ]);
        const hasTerminalOutcome = (outcomesSummary || []).some((o) =>
          o.dcp_job_id === dcpId
          && venueIds.includes(o.venue_id)
          && TERMINAL_OUTCOMES.has(o.outcome),
        );
        const isPastScreening = (lastWindowTs != null && lastWindowTs < startOfTodayMs)
          || hasTerminalOutcome;
        // dcpCreatedAt drives the "superseded" detection below — when
        // the operator re-cuts a master and issues a new KDM for the
        // same recipient, the older DCP's card should auto-hide.
        const dcpCreatedAt = dcp?.created_at ? new Date(dcp.created_at).getTime() : 0;
        return { ...r, dcp, dcpId, lastTs, nextScreeningTs, lastWindowTs, isPastScreening, dcpCreatedAt };
      });
      out.push(...cards);
    }
    // v10.18.28: synthesize cards for venues whose only record is in
    // the audit log (parent DCP cancelled, kdm_records hard-deleted).
    // These render inline alongside live recipient cards instead of
    // in a separate "Historical" section. Marked isPastScreening
    // because the screening already happened. The synthesized card
    // carries `isHistoricalAuditOnly` so the renderer skips the
    // download/revoke actions (there's no live data to act on).
    {
      const activeVenueIds = new Set(out.map((c) => c.venueId).filter(Boolean));
      for (const pdv of (pastDeliveriesByVenue || [])) {
        if (activeVenueIds.has(pdv.venue_id)) continue;
        const lastTs = pdv.last_sent_at ? new Date(pdv.last_sent_at).getTime() : 0;
        out.push({
          label: pdv.venue_name,
          email: pdv.past_sends?.[pdv.past_sends.length - 1]?.sent_to || '',
          kdms: [],
          shares: [],
          venueId: pdv.venue_id,
          dcp: null,
          dcpId: null,
          lastTs,
          nextScreeningTs: lastTs || null,
          lastWindowTs: lastTs || null,
          isPastScreening: true,
          dcpCreatedAt: 0,
          isHistoricalAuditOnly: true,
          historicalSends: pdv.past_sends || [],
          historicalChain: pdv.chain || null,
        });
      }
    }

    // v10.7.2: mark older-DCP cards as superseded when the SAME recipient
    // (same email lowercased) has a NEWER DCP card. The newer one stays
    // visible; the older one gets a flag the filter below uses to hide
    // it from the default view. Different recipients on the same DCP are
    // independent — superseded is per-recipient.
    {
      const byEmail = new Map();
      for (const c of out) {
        const k = (c.email || '').trim().toLowerCase();
        if (!k) continue;
        if (!byEmail.has(k)) byEmail.set(k, []);
        byEmail.get(k).push(c);
      }
      for (const cards of byEmail.values()) {
        if (cards.length <= 1) continue;
        const latestTs = Math.max(...cards.map((c) => c.dcpCreatedAt || 0));
        for (const c of cards) {
          c.isSuperseded = (c.dcpCreatedAt || 0) < latestTs;
        }
      }
    }
    // Order: cards with no screening date (e.g. press-screening shares
    // with no KDM) sit at top by recent activity; then upcoming
    // screenings ascending (next first); then past screenings
    // descending (most recent past first) when surfaced via "show past".
    out.sort((a, b) => {
      if (a.isPastScreening !== b.isPastScreening) return a.isPastScreening ? 1 : -1;
      if (a.isPastScreening) {
        return (b.nextScreeningTs ?? 0) - (a.nextScreeningTs ?? 0);
      }
      const aHas = a.nextScreeningTs != null;
      const bHas = b.nextScreeningTs != null;
      if (aHas && bHas) return a.nextScreeningTs - b.nextScreeningTs;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return b.lastTs - a.lastTs;
    });
    return out;
  }, [packages, dcpJobs, kdmRecords, bookings, pastDeliveriesByVenue, venuesForEmailLookup, outcomesSummary]);

  const pastCardCount = useMemo(
    () => recipientCards.filter((c) => c.isPastScreening).length,
    [recipientCards],
  );
  const supersededCount = useMemo(
    () => recipientCards.filter((c) => c.isSuperseded && !c.isPastScreening).length,
    [recipientCards],
  );

  const filteredCards = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = recipientCards;
    if (!showPast) list = list.filter((c) => !c.isPastScreening);
    if (!showSuperseded) list = list.filter((c) => !c.isSuperseded);
    if (!q) return list;
    return list.filter((c) =>
      (c.label || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.dcp?.name || '').toLowerCase().includes(q)
      || c.shares.some((s) => (s.token || '').toLowerCase().includes(q)),
    );
  }, [recipientCards, filter, showPast, showSuperseded]);

  const filteredKdms = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = kdmRecords;
    // v11.0.0: KDMs whose `booking_last_screening` (or `valid_to` as
    // fallback) is before today are past-screening KDMs and respect
    // the same `showPast` toggle the Distributions tab honors. Pre-fix
    // the KDMs tab rendered every historical record by default — the
    // operator audit called this out ("I still notice past screenings
    // in the Distribution window"). Status filter, search, and the
    // `expired` chip still work the same on top.
    const startOfTodayMs = (() => {
      const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime();
    })();
    if (!showPast) {
      list = list.filter((k) => {
        const lastTs = k.booking_last_screening
          ? new Date(k.booking_last_screening).getTime()
          : (k.valid_to ? new Date(k.valid_to).getTime() : null);
        return lastTs == null || lastTs >= startOfTodayMs;
      });
    }
    if (q) {
      list = list.filter((k) =>
        (k.venue_name || '').toLowerCase().includes(q)
        || (k.screen_name || '').toLowerCase().includes(q)
        || (k.tech_contact_email || '').toLowerCase().includes(q)
        || (dcpJobs.find((j) => j.id === k.dcp_job_id)?.name || '').toLowerCase().includes(q),
      );
    }
    if (kdmStatusFilter) {
      list = list.filter((k) => kdmExpiryStage(k) === kdmStatusFilter);
    }
    return list;
  }, [kdmRecords, dcpJobs, filter, kdmStatusFilter, showPast]);

  // Sortable KDMs table — fixed insertion order before this. Stage
  // sorts pending → active → expiring → expired → revoked so the
  // operator's "what needs my attention" rises to the top.
  const STAGE_RANK = { pending: 0, active: 1, expiring: 2, expired: 3, revoked: 4 };
  const { rows: sortedKdms, sortProps: kdmSortProps } = useSortableTable(
    filteredKdms,
    'valid_to',
    'asc',
    {
      dcp_name: (k) => (dcpJobs.find((j) => j.id === k.dcp_job_id)?.name || '').toLowerCase(),
      venue: (k) => `${k.venue_name || ''} ${k.screen_name || ''}`.toLowerCase(),
      valid_to: (k) => (k.valid_to ? new Date(k.valid_to).getTime() : 0),
      stage: (k) => STAGE_RANK[kdmExpiryStage(k)] ?? 99,
    },
  );

  // Bulk ops visibility + select-all-visible. Only operates within
  // the KDMs tab — bulk ops on the recipient-card view doesn't have
  // a coherent action surface (cards are heterogeneous).
  const bulkVisible = tab === 'kdms' && shouldShowDistributionBulkOps(
    sortedKdms.length, !!userInfo?.is_superadmin, bulkForce,
  );
  const visibleKdmIds = sortedKdms.map((k) => k.id);

  // Wrap the runner so each bulk button can `await` and close the
  // confirm dialog when done.
  const runBulk = async (action) => {
    await bulk.run(action);
    setBulkAction(null);
  };

  const doBulkResend = () => runBulk(async (id) => {
    await api.post(`/api/kdm/records/${id}/email`, {});
  });
  const doBulkDelete = () => runBulk((id) => dispatch(deleteKDMRecord(id)).unwrap());

  const onCopy = (msg, severity = 'success') => setSnackbar({ open: true, message: msg, severity });
  const onResend = async (shareId) => {
    try {
      await api.post(`/api/delivery/shares/${shareId}/resend`);
      onCopy('Email re-sent');
      dispatch(fetchPackages({ sourceType: 'dcp' }));
    } catch (e) { onCopy(e.response?.data?.detail || 'Re-send failed', 'error'); }
  };
  const onRevoke = async (shareId, recipientLabel) => {
    if (!window.confirm(`Revoke the delivery link for ${recipientLabel}? They will no longer be able to download. The link's audit history is preserved.`)) return;
    try {
      await dispatch(revokeShare(shareId)).unwrap();
      dispatch(fetchPackages({ sourceType: 'dcp' }));
      onCopy('Share revoked');
    } catch (e) { onCopy(typeof e === 'string' ? e : 'Revoke failed', 'error'); }
  };

  return (
    <Box>
      <PipelineProgress />
      <PageHeader
        title="Distribution"
        subtitle="Send DCPs to recipients, generate KDMs, and audit every delivery."
      />

      {/* v11.0.0: Calendar → Distribution link. Bookings in the next
          7 days that don't yet have a successful Share against the
          booked DCP are surfaced as a yellow alert. Operator audit:
          "the calendar should inform the Distribution page of
          upcoming requirements with alerts if a theater has not been
          successfully delivered to." */}
      <UpcomingScreeningsAlert
        bookings={bookings}
        kdmRecords={kdmRecords}
        packages={packages}
        dcpJobs={dcpJobs}
        onJumpToDcp={(jobId) => {
          const j = dcpJobs.find((d) => d.id === jobId);
          if (j) {
            setPickedDCP(j);
            setSearchParams((sp) => { sp.set('dcp', String(jobId)); return sp; });
            setDistributeOpen(true);
          }
        }}
      />

      {/* v10.21.57: live in-flight transfers surface here so the
          operator sees them right where deliveries are managed. The
          panel polls /api/delivery/active-transfers itself and hides
          when nothing is moving — no empty state. Moved from the DCP
          Library page where it was buried under the archive view. */}
      <ActiveTransfersPanel sourceType="dcp" />

      {/* Aggregate stats — KDM expiry pressure is the operational
          signal this page is for, so it leads. Click counters to
          jump-filter the KDMs tab. */}
      {kdmRecords.length > 0 && (() => {
        const a = computeKdmAggregates(kdmRecords);
        return (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
            mb: 2, p: 1.5, borderRadius: 1,
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Issued today</Typography>
              <Typography variant="h6">{a.issuedToday}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Active</Typography>
              <Typography variant="h6" color="success.main">{a.active}</Typography>
            </Box>
            {a.expiring > 0 && (
              <Tooltip title="KDMs expiring within 7 days. Click to filter.">
                <Box sx={{ cursor: 'pointer' }} onClick={() => { setTab('kdms'); setKdmStatusFilter('expiring'); setSearchParams((sp) => { sp.set('tab', 'kdms'); return sp; }); }}>
                  <Typography variant="caption" color="text.secondary">Expiring 7d</Typography>
                  <Typography variant="h6" color="warning.main">{a.expiring}</Typography>
                </Box>
              </Tooltip>
            )}
            {a.expired > 0 && (
              <Tooltip title="Already expired. Click to filter.">
                <Box sx={{ cursor: 'pointer' }} onClick={() => { setTab('kdms'); setKdmStatusFilter('expired'); setSearchParams((sp) => { sp.set('tab', 'kdms'); return sp; }); }}>
                  <Typography variant="caption" color="text.secondary">Expired</Typography>
                  <Typography variant="h6" color="error.main">{a.expired}</Typography>
                </Box>
              </Tooltip>
            )}
            {a.pending > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="h6" color="info.main">{a.pending}</Typography>
              </Box>
            )}
            {a.revoked > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Revoked</Typography>
                <Typography variant="h6" color="text.secondary">{a.revoked}</Typography>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* DCP picker + Distribute action. v10.21.57: the empty-state
          "Create DCP" card and the inline "New DCP" outlined button
          (both added in v10.21.55) are gone — the sidebar's Create
          link under Theatrical DCP is the canonical path to encode a
          new DCP, surfacing it here was duplicating navigation. The
          empty-state copy still tells the operator what's missing,
          just without an action button. */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {completedDCPs.length === 0 ? (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              No completed DCPs in this workspace yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use Create in the sidebar to encode a new DCP. It will appear in the picker as soon as it finishes packaging.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Create distribution:</Typography>
            <Autocomplete
              size="small"
              sx={{ minWidth: 360, flex: 1 }}
              options={completedDCPs}
              value={pickedDCP}
              onChange={(_e, v) => {
                setPickedDCP(v);
                if (v) setSearchParams((sp) => { sp.set('dcp', String(v.id)); return sp; });
                else setSearchParams((sp) => { sp.delete('dcp'); return sp; });
              }}
              getOptionLabel={(j) => {
                const base = `${j.name || `DCP #${j.id}`}${j.encrypt ? ' 🔐' : ''}`;
                const showAt = nextScreeningByDcp[j.id];
                return showAt ? `${base} · ${fmtShowDateTime(showAt)}` : base;
              }}
              renderOption={(props, j) => {
                const showAt = nextScreeningByDcp[j.id];
                return (
                  <li {...props} key={j.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {j.name || `DCP #${j.id}`}{j.encrypt ? ' 🔐' : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ color: showAt ? 'primary.main' : 'text.secondary' }}>
                        {showAt
                          ? `Next show · ${fmtShowDateTime(showAt)}`
                          : 'No upcoming screening'}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} placeholder="Pick a DCP…" />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="No matches"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!pickedDCP}
              onClick={() => setDistributeOpen(true)}
            >
              Distribute
            </Button>
          </>
        )}
        {!!userInfo?.is_superadmin && kdmRecords.length < 10 && (
          <Tooltip title="Operator-only: force show bulk-ops UI on small fixture workspaces">
            <FormControlLabel
              control={<Switch size="small" checked={bulkForce} onChange={toggleBulkForce} />}
              label="Bulk ops"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            />
          </Tooltip>
        )}
        <Tooltip title="Refresh">
          <IconButton onClick={() => {
            dispatch(fetchPackages({ sourceType: 'dcp' }));
            dispatch(fetchKDMRecords({}));
          }}><RefreshIcon /></IconButton>
        </Tooltip>
      </Paper>

      {/* v11.0.0 — Outstanding Requests moved BELOW the main list per
          operator audit: "it visually interrupts the primary workflow.
          Use the same table structure but a different surface." */}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(_e, v) => { setTab(v); setSearchParams((sp) => { if (v === 'kdms') sp.set('tab', 'kdms'); else sp.delete('tab'); return sp; }); }}>
          <Tab value="distributions" label={`Sent Deliveries (${filteredCards.length})`} />
          <Tab value="kdms" label={`KDM Keys (${filteredKdms.length})`} />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {tab === 'kdms' && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={kdmStatusFilter}
              onChange={(e) => setKdmStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expiring">Expiring 7d</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </Select>
          </FormControl>
        )}
        {tab === 'distributions' && pastCardCount > 0 && (
          <Tooltip title="Show distributions whose last screening day has already passed">
            <FormControlLabel
              control={<Switch size="small" checked={showPast} onChange={(_e, v) => setShowPast(v)} />}
              label={`Show past (${pastCardCount})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            />
          </Tooltip>
        )}
        {tab === 'distributions' && supersededCount > 0 && (
          <Tooltip title="A newer DCP exists for the same recipient — the older one is hidden by default. Turn this on to audit or revoke old shares.">
            <FormControlLabel
              control={<Switch size="small" checked={showSuperseded} onChange={(_e, v) => setShowSuperseded(v)} />}
              label={`Show superseded (${supersededCount})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            />
          </Tooltip>
        )}
        <TextField
          size="small"
          placeholder={tab === 'kdms' ? 'Search venue, DCP, screen…' : 'Search recipient, DCP, venue…'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Box>

      {tab === 'distributions' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredCards.map((c, idx) => {
            // v10.18.28: synthesized cards from past_deliveries audit
            // log render via the simpler HistoricalAuditRecipientCard.
            if (c.isHistoricalAuditOnly) {
              return (
                <HistoricalAuditRecipientCard key={`hist-${c.venueId}-${idx}`} recipient={c} />
              );
            }
            const dcpKdms = (kdmRecords || []).filter((r) => r.dcp_job_id === c.dcpId);
            const physKey = (c.dcpId && c.venueId) ? `${c.dcpId}:${c.venueId}` : null;
            const physicalDelivery = physKey ? physicalDeliveryByKey.get(physKey) : null;
            return (
              <RecipientCard
                key={`${c.dcpId}-${idx}`}
                recipient={c}
                dcp={c.dcp}
                dcpKdms={dcpKdms}
                physicalDelivery={physicalDelivery}
                transfersByShare={transfersByShare}
                fetchTransfersFor={fetchTransfersFor}
                onCopy={onCopy}
                onResend={onResend}
                onRevoke={onRevoke}
                onEmailKdm={(kdm) => { setKdmEmailDialog(kdm); setKdmEmailForm({ cc: '', message: '' }); }}
                onDeleteKdm={(kdm) => setDeleteKdmConfirm(kdm)}
                onComposeEmail={(arg) => setComposer(arg)}
              />
            );
          })}
          {filteredCards.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {recipientCards.length === 0
                  ? 'No distributions yet. Pick a DCP above and click Distribute.'
                  : (!showPast && pastCardCount > 0 && !filter.trim())
                    ? `No upcoming distributions. ${pastCardCount} past distribution${pastCardCount === 1 ? '' : 's'} hidden — toggle "Show past" to view.`
                    : 'No distributions match the current filter.'}
              </Typography>
            </Paper>
          )}

        </Box>
      )}

      {bulkVisible && (
        <BulkActionBar
          selectedCount={selectedKdmIds.size}
          busy={bulk.busy}
          progress={bulk.progress}
          onClear={bulk.clearSelection}
          actions={
            <>
              <Button size="small" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'resend' })}>
                Resend KDM emails
              </Button>
              <Button size="small" color="error" variant="contained" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'delete' })}>
                Delete
              </Button>
            </>
          }
        />
      )}

      {tab === 'kdms' && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {bulkVisible && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={bulk.someVisibleSelected(visibleKdmIds) && !bulk.allVisibleSelected(visibleKdmIds)}
                      checked={bulk.allVisibleSelected(visibleKdmIds)}
                      onChange={() => bulk.toggleSelectAllVisible(visibleKdmIds)}
                      disabled={sortedKdms.length === 0}
                    />
                  </TableCell>
                )}
                <TableCell><TableSortLabel {...kdmSortProps('dcp_name')}>DCP</TableSortLabel></TableCell>
                <TableCell><TableSortLabel {...kdmSortProps('venue')}>Venue / Screen</TableSortLabel></TableCell>
                <TableCell><TableSortLabel {...kdmSortProps('valid_to')}>Validity</TableSortLabel></TableCell>
                <TableCell sx={{ width: 130 }}><TableSortLabel {...kdmSortProps('stage')}>Status</TableSortLabel></TableCell>
                <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedKdms.map((record) => {
                const dcp = dcpJobs.find((j) => j.id === record.dcp_job_id);
                const cfg = kdmStatusChip(record.status);
                return (
                  <TableRow key={record.id} hover selected={selectedKdmIds.has(record.id)}>
                    {bulkVisible && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedKdmIds.has(record.id)}
                          onChange={() => bulk.toggleSelected(record.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{dcp?.name || `DCP #${record.dcp_job_id}`}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{record.venue_name || '—'}</Typography>
                      {record.screen_name && (
                        <Typography variant="caption" color="text.secondary">{record.screen_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {record.valid_from ? new Date(record.valid_from).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'} —
                        {' '}
                        {record.valid_to ? new Date(record.valid_to).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={cfg.label} color={cfg.color} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Download KDM bundle">
                        <span>
                          <IconButton size="small" disabled={record.status !== 'completed'} onClick={() => dispatch(downloadKDM(record.id))}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={record.tech_contact_email ? `Email to ${record.tech_contact_email}` : 'No tech contact email on file'}>
                        <span>
                          <IconButton size="small" disabled={record.status !== 'completed' || !record.tech_contact_email} onClick={() => { setKdmEmailDialog(record); setKdmEmailForm({ cc: '', message: '' }); }}>
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete KDM record">
                        <IconButton size="small" color="error" onClick={() => setDeleteKdmConfirm(record)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedKdms.length === 0 && (
                <TableRow><TableCell colSpan={bulkVisible ? 6 : 5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {kdmRecords.length === 0 ? 'No KDMs generated yet.' : 'No KDMs match the current filter.'}
                  </Typography>
                  {(filter || kdmStatusFilter) && (
                    <Button
                      size="small"
                      onClick={() => { setFilter(''); setKdmStatusFilter(''); }}
                      sx={{ mt: 1 }}
                    >
                      Clear filters
                    </Button>
                  )}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* v11.0.0 — Outstanding Requests demoted to below the main
          list. It's a useful tracker but it's not the primary action
          on this page, and audit said it visually interrupted the
          flow when sandwiched between picker and tabs. */}
      <Box sx={{ mt: 4 }}>
        <OutstandingVenueRequestsTable
          onSnackbar={({ message, severity }) => setSnackbar({ open: true, message, severity: severity || 'success' })}
        />
      </Box>

      {distributeOpen && pickedDCP && (
        <DCPDistributeDialog
          open={distributeOpen}
          dcp={pickedDCP}
          onClose={() => setDistributeOpen(false)}
          onSuccess={() => {
            // Don't auto-close — the dialog shows the per-target
            // results table after a successful submit and the operator
            // wants to scan it before dismissing. Just refresh the
            // parent's data so the KDM/Package lists are current when
            // they do close.
            dispatch(fetchPackages({ sourceType: 'dcp' }));
            dispatch(fetchKDMRecords({}));
            setSnackbar({ open: true, message: 'Distribution created', severity: 'success' });
          }}
        />
      )}

      {/* KDM email dialog (CC + note) — same UX the Library used to surface. */}
      <Dialog open={!!kdmEmailDialog} onClose={() => setKdmEmailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Email KDM to {kdmEmailDialog?.venue_name}{kdmEmailDialog?.screen_name ? ` — ${kdmEmailDialog.screen_name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sending to: <strong>{kdmEmailDialog?.tech_contact_email || '(no tech contact email)'}</strong>
            </Typography>
            <TextField
              label="CC"
              fullWidth
              value={kdmEmailForm.cc}
              onChange={(e) => setKdmEmailForm((f) => ({ ...f, cc: e.target.value }))}
              placeholder="Optional — comma-separate for multiple"
              InputLabelProps={{ shrink: Boolean(kdmEmailForm.cc) || undefined }}
            />
            <TextField
              label="Note to venue"
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={kdmEmailForm.message}
              onChange={(e) => setKdmEmailForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="e.g. Please ingest this KDM before the screening."
              InputLabelProps={{ shrink: Boolean(kdmEmailForm.message) || undefined }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKdmEmailDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              await api.post(`/api/kdm/records/${kdmEmailDialog.id}/email`, {
                cc_email: kdmEmailForm.cc || undefined,
                message: kdmEmailForm.message || undefined,
              });
              setSnackbar({ open: true, message: `KDM emailed to ${kdmEmailDialog.venue_name}${kdmEmailDialog.screen_name ? ` / ${kdmEmailDialog.screen_name}` : ''}`, severity: 'success' });
              setKdmEmailDialog(null);
              setKdmEmailForm({ cc: '', message: '' });
            } catch (e) {
              setSnackbar({ open: true, message: e?.response?.data?.detail || 'Failed to send', severity: 'error' });
            }
          }}>Send KDM</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!bulkAction} onClose={() => setBulkAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkAction?.kind === 'resend' && `Resend ${selectedKdmIds.size} KDM emails?`}
          {bulkAction?.kind === 'delete' && `Delete ${selectedKdmIds.size} KDMs?`}
        </DialogTitle>
        <DialogContent>
          {bulkAction?.kind === 'resend' && (
            <Typography variant="body2">
              Re-sends each KDM email to its venue's tech contact. Useful for
              chasing venues whose ingest scanner missed the first delivery
              (Christie/Dolby/GDC ingest scanners hide unmatched KDMs from the
              "Key Messages" view).
            </Typography>
          )}
          {bulkAction?.kind === 'delete' && (
            <Typography variant="body2">
              Deletes each KDM record + its `.kdm.xml` sidecar on disk. The
              recipient's projector keeps the previously-ingested copy until
              they remove it locally — re-issuance creates a fresh KDM with
              the same validity window. Cannot be undone.
            </Typography>
          )}
          <Box sx={{ mt: 2, p: 1.5, maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {Array.from(selectedKdmIds).slice(0, 50).map((id) => {
              const k = kdmRecords.find((x) => x.id === id);
              if (!k) return null;
              const dcpName = dcpJobs.find((j) => j.id === k.dcp_job_id)?.name || `DCP #${k.dcp_job_id}`;
              return (
                <Typography key={id} variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  {dcpName} → {k.venue_name || '—'}{k.screen_name ? ` · ${k.screen_name}` : ''}
                </Typography>
              );
            })}
            {selectedKdmIds.size > 50 && (
              <Typography variant="caption" color="text.secondary">
                …and {selectedKdmIds.size - 50} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAction(null)} disabled={bulk.busy}>Cancel</Button>
          <Button
            color={bulkAction?.kind === 'delete' ? 'error' : 'primary'}
            variant="contained"
            disabled={bulk.busy}
            onClick={() => {
              if (bulkAction?.kind === 'resend') doBulkResend();
              else if (bulkAction?.kind === 'delete') doBulkDelete();
            }}
          >
            {bulkAction?.kind === 'delete' ? 'Delete' : 'Resend'} {selectedKdmIds.size}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteKdmConfirm)} onClose={() => setDeleteKdmConfirm(null)}>
        <DialogTitle>Delete KDM?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete KDM for {deleteKdmConfirm?.venue_name}{deleteKdmConfirm?.screen_name ? ` — ${deleteKdmConfirm.screen_name}` : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteKdmConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try {
              await dispatch(deleteKDMRecord(deleteKdmConfirm.id)).unwrap();
              setSnackbar({ open: true, message: 'KDM record deleted', severity: 'success' });
            } catch (e) {
              setSnackbar({ open: true, message: typeof e === 'string' ? e : 'Delete failed', severity: 'error' });
            }
            setDeleteKdmConfirm(null);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      <EmailComposer
        open={!!composer}
        onClose={() => setComposer(null)}
        touchpoint={composer?.touchpoint}
        subjectId={composer?.subjectId}
        contextLabel={composer?.contextLabel}
      />
    </Box>
  );
}
